import { CampaignListCard } from '@atlas/ui-domain'
import { toCampaignListItems } from '@/adapters/campaign-list-item'
import type { Campaign } from '@/domain/campaign.types'

/** The brand page's own heading for the campaigns card - the brand's campaigns, not one product's. */
export const BRAND_CAMPAIGNS_TITLE = 'Active campaigns'

/**
 * Campaigns running for this brand — the shared campaigns card under the brand page's own
 * heading. Real data: campaigns are read for the brand through the same applicable-campaigns
 * read the product page uses, so the two surfaces cannot list a campaign differently.
 *
 * Deliberately NOT narrowed by the page's market, even when one is applied: the brand-page
 * contract returns `market` as a single code and exposes no per-axis applicability ladder,
 * so the client cannot tell a campaign classified to the REGION above that market (which
 * applies) from one classified to a SIBLING market (which does not). Narrowing on the code
 * alone would drop the first kind. The per-market question belongs to the product page,
 * whose contract does resolve a ladder.
 */
export interface ActiveCampaignsCardProps {
	campaigns: Campaign[]
	onOpenCampaign: (campaignId: string) => void
	/** Called instead of navigating when the heading action is followed. */
	onViewAllCampaigns?: () => void
	isLoading?: boolean
	isError?: boolean
}

export function ActiveCampaignsCard({
	campaigns,
	onOpenCampaign,
	onViewAllCampaigns,
	isLoading,
	isError,
}: ActiveCampaignsCardProps) {
	return (
		<CampaignListCard
			campaigns={toCampaignListItems(campaigns)}
			title={BRAND_CAMPAIGNS_TITLE}
			viewAllHref="/campaigns"
			onViewAll={onViewAllCampaigns}
			onOpenCampaign={onOpenCampaign}
			isLoading={isLoading}
			isError={isError}
			emptyLabel="No campaigns are classified to this brand yet."
		/>
	)
}
